import { enhanceIntradayChangeValue } from "./intraday"

export const VALUE_FORMATS = {
  // Percentages sometimes have the % symbol included depending on the renderer,
  // so they aren't strictly a number format.
  PERCENT: "percent",
  // Our percentages are provided by the API as a value between 0 - 1, but
  // we sometimes multiply this by 100 to display percentages between 0 - 100 in the application.
  // However, we don't want to multiply by 100 again for the download format.
  PERCENT_100: "percent100",
  NUMBER: "number",
  STRING: "string",
  CURRENCY: "currency",
}

export const COMPOSITION_METRIC_IDS = ["marketValue", "percentEquity"]

export const ASSET_METRIC_IDS = [
  ...COMPOSITION_METRIC_IDS,
  "totalReturn",
  "specificReturn",
  "factorsReturn",
  "tradingReturn",
  "riskTotalContribution",
  "riskSpecificContribution",
  "riskFactorsContribution",
  "predictedBeta",
  "historicalBeta",
  "dynamicPredictedBeta",
]

export const METRICS = {
  PERFORMANCE: "performance",
  RISK: "risk",
  EXPOSURE: "exposure",
  BETA: "beta",
}

export const FACTOR_VIEWS = {
  ALL: "op_all",
}

export const METRIC_TYPES = {
  NET_CONTRIBUTION: "netContribution",
  Z_SCORE: "assetZScore",
  CURRENCY_EXPOSURE: "currencyExposure",
  CURRENCY_PERFORMANCE: "currencyPerformance",
  RISK_DECOMPOSITION: "riskDecomposition",
  STANDALONE: "standalone",
  CUMULATIVE_PERCENT: "cumulativePercent",
}

export const RESIDUAL_RETURN = "residualReturn"

export const INTRADAY_METRIC_CATEGORY = {
  id: "intraday",
  label: "Intraday Metrics - Fixed Periods",
  description: "Uses the latest available intraday market data. These columns may not follow the dashboard date range when viewing historical periods.",
  items: [
    {
      id: "standaloneTotal",
      label: "Standalone Returns — Total",
      category: "Intraday",
      excludeGroupIdFromChildMetricPaths: true,
      items: [
        {
          id: "standaloneReturn1d",
          isPerformanceMetric: true,
          label: "Today Return",
          category: "Intraday",
          dataPath: "intradayStandalone1d.returnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Performance 1D",
          hideInstrumentLabel: true,
        },
        {
          id: "standaloneReturn1w",
          isPerformanceMetric: true,
          label: "1W Return",
          category: "Intraday",
          dataPath: "intradayStandalone1w.returnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Performance 1W",
          hideInstrumentLabel: true,
        },
        {
          id: "standaloneReturn1m",
          isPerformanceMetric: true,
          label: "1M Return",
          category: "Intraday",
          dataPath: "intradayStandalone1m.returnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Performance 1M",
          hideInstrumentLabel: true,
        },
      ],
    },
    {
      id: "standaloneFactor",
      label: "Standalone Returns — Factor",
      category: "Intraday",
      excludeGroupIdFromChildMetricPaths: true,
      items: [
        {
          id: "standaloneFactorsReturn1d",
          label: "Today Factor Return",
          category: "Intraday",
          dataPath: "intradayStandalone1d.factorsReturnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Factor Attribution 1D",
          hideInstrumentLabel: true,
        },
        {
          id: "standaloneFactorsReturn1w",
          label: "1W Factor Return",
          category: "Intraday",
          dataPath: "intradayStandalone1w.factorsReturnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Factor Attribution 1W",
          hideInstrumentLabel: true,
        },
        {
          id: "standaloneFactorsReturn1m",
          label: "1M Factor Return",
          category: "Intraday",
          dataPath: "intradayStandalone1m.factorsReturnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Factor Attribution 1M",
          hideInstrumentLabel: true,
        },
      ],
    },
    {
      id: "standaloneSpecific",
      label: "Standalone Returns — Specific",
      category: "Intraday",
      excludeGroupIdFromChildMetricPaths: true,
      items: [
        {
          id: "standaloneSpecificReturn1d",
          label: "Today Specific Return",
          category: "Intraday",
          dataPath: "intradayStandalone1d.specificReturnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Specific Attribution 1D",
          hideInstrumentLabel: true,
        },
        {
          id: "standaloneSpecificReturn1w",
          label: "1W Specific Return",
          category: "Intraday",
          dataPath: "intradayStandalone1w.specificReturnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Specific Attribution 1W",
          hideInstrumentLabel: true,
        },
        {
          id: "standaloneSpecificReturn1m",
          label: "1M Specific Return",
          category: "Intraday",
          dataPath: "intradayStandalone1m.specificReturnPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Standalone Specific Attribution 1M",
          hideInstrumentLabel: true,
        },
      ],
    },
    {
      id: "contributionTotal",
      label: "Portfolio Contribution — Total",
      category: "Intraday",
      excludeGroupIdFromChildMetricPaths: true,
      items: [
        {
          id: "contribution1d",
          label: "Today $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1d.contributionCurrency",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Contribution Performance 1D",
        },
        {
          id: "contributionPercent1d",
          isPerformanceMetric: true,
          label: "Today % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1d.contributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Contribution Performance 1D",
        },
        {
          id: "contribution1w",
          label: "1W $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1w.contributionCurrency",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Contribution Performance 1W",
        },
        {
          id: "contributionPercent1w",
          isPerformanceMetric: true,
          label: "1W % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1w.contributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Contribution Performance 1W",
        },
        {
          id: "contribution1m",
          label: "1M $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1m.contributionCurrency",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Contribution Performance 1M",
        },
        {
          id: "contributionPercent1m",
          isPerformanceMetric: true,
          label: "1M % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1m.contributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Contribution Performance 1M",
        },
      ],
    },
    {
      id: "contributionFactor",
      label: "Portfolio Contribution — Factor",
      category: "Intraday",
      excludeGroupIdFromChildMetricPaths: true,
      items: [
        {
          id: "factorsContribution1d",
          label: "Today Factor $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1d.factorsContribution",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Factor Attribution 1D",
        },
        {
          id: "factorsContributionPercent1d",
          label: "Today Factor % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1d.factorsContributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Factor Attribution 1D",
        },
        {
          id: "factorsContribution1w",
          label: "1W Factor $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1w.factorsContribution",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Factor Attribution 1W",
        },
        {
          id: "factorsContributionPercent1w",
          label: "1W Factor % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1w.factorsContributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Factor Attribution 1W",
        },
        {
          id: "factorsContribution1m",
          label: "1M Factor $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1m.factorsContribution",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Factor Attribution 1M",
        },
        {
          id: "factorsContributionPercent1m",
          label: "1M Factor % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1m.factorsContributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Factor Attribution 1M",
        },
      ],
    },
    {
      id: "contributionSpecific",
      label: "Portfolio Contribution — Specific",
      category: "Intraday",
      excludeGroupIdFromChildMetricPaths: true,
      items: [
        {
          id: "specificContribution1d",
          label: "Today Specific $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1d.specificContribution",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Specific Attribution 1D",
        },
        {
          id: "specificContributionPercent1d",
          label: "Today Specific % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1d.specificContributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Specific Attribution 1D",
        },
        {
          id: "specificContribution1w",
          label: "1W Specific $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1w.specificContribution",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Specific Attribution 1W",
        },
        {
          id: "specificContributionPercent1w",
          label: "1W Specific % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1w.specificContributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Specific Attribution 1W",
        },
        {
          id: "specificContribution1m",
          label: "1M Specific $ Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1m.specificContribution",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
          tooltipMetricType: "Intraday Specific Attribution 1M",
        },
        {
          id: "specificContributionPercent1m",
          label: "1M Specific % Contribution",
          category: "Intraday",
          dataPath: "intradayContribution1m.specificContributionPercent",
          format: VALUE_FORMATS.PERCENT,
          singleFactor: true,
          tooltipMetricType: "Intraday Specific Attribution 1M",
        },
      ],
    },
    {
      id: "securityPricing",
      label: "Security Pricing",
      category: "Intraday",
      excludeGroupIdFromChildMetricPaths: true,
      items: [
        {
          id: "prices",
          label: "Current Price",
          category: "Intraday",
          dataPath: "intraday.price",
          format: VALUE_FORMATS.CURRENCY,
          singleFactor: true,
        },
        {
          id: "percentChange",
          isPerformanceMetric: true,
          label: "Price Change",
          category: "Intraday",
          dataPath: "intraday.change_percent",
          format: VALUE_FORMATS.PERCENT_100,
          singleFactor: true,
          enhanceValue: enhanceIntradayChangeValue,
        },
      ],
    },
  ],
}

export const PERFORMANCE_CONTRIBUTION_CATEGORY_CURRENCY = "$ Performance Contribution"
export const PERFORMANCE_CONTRIBUTION_CATEGORY_PERCENT = "% Performance Contribution"
